// =============================================================================
// TopUpPickerView.jsx — the DEDICATED picker view of ONE Stock-Top-up Sales Order.
// =============================================================================
// Rendered by RequestDetail when a picker opens a `stock_top_up` request from the
// Pickers List with a scoped SO (?picker_so). A top-up has no children, so this is
// NOT PickerRequestView (which is children-based) — it sources its ONE card from the
// SO line itself. It reuses the fit-out picker's SO-LINE writers unchanged
// (updateSoLinePrices → unit_price, appendOrderNumbersToSoLines → the line comment),
// then releases the held push (hold → queued) and actions the picker task.
//
//   • per_unit pack SO  → one card, the single pack
//   • summary bulk SO   → one card showing the ITEMIZED breakdown (item + qty) so the
//                         picker knows exactly what to order, + one Amount + Order No.
//   • amount SO ($150)  → one card, the reimbursement line
//
// Reference: docs/stock-top-ups-plan.md §H2.
// =============================================================================
import React from "react";
import { completePickerTaskBySoNumber, findPickerLockTask } from "../../data/requestTasks";
import { getPushByOrderNumber, releaseHeldPushByOrderNumber } from "../../data/salesOrderPushes";
import { getSalesOrderByNumber } from "../../data/salesOrders";
import { getStockTopUpHeader, listStockTopUpLines } from "../../data/stockTopUp";
import { listTopUpSoRules } from "../../data/topUpSoRules";
import {
  appendOrderNumbersToSoLines,
  postOrderConfirmationOnce,
  updateSoLinePrices,
} from "../../services/pickerCompletion";
import { lineLabel, sectionUnitLabel } from "../../services/stockTopUpForm";
import { logAudit } from "../../utils/auditLog";
import { formatPhone } from "../../utils/sanitize";
import { DeliveryInstruction } from "./DeliveryInstruction.jsx";
import { NOT_YOUR_TASK_MSG, TASK_NOT_CLOSED_MSG } from "./pickerCompleteMessages";

const BLUE = "#1E88E5";
const BORDER = "#CFE0DE";
const fieldLabel = {
  fontSize: 11,
  fontWeight: 700,
  color: "#607D79",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  marginBottom: 4,
};
const roValue = {
  fontSize: 14,
  color: "#1A2E2B",
  background: "#F7F9F9",
  border: `1px solid ${BORDER}`,
  borderRadius: 7,
  padding: "9px 12px",
  minHeight: 20,
};
const editInput = {
  width: "100%",
  fontSize: 14,
  color: "#1A2E2B",
  background: "white",
  border: "2px solid #00897B",
  borderRadius: 7,
  padding: "8px 12px",
  boxSizing: "border-box",
  fontFamily: "inherit",
};
const thItem = {
  textAlign: "left",
  fontSize: 12,
  color: "#607D79",
  fontWeight: 700,
  padding: "6px 8px",
  borderBottom: `1px solid ${BORDER}`,
};
// Qty sits in its OWN narrow column so the "Qty" heading is directly above the number;
// the pack-unit chip trails in a shrink-to-content column to its right.
const thQty = { ...thItem, textAlign: "right", width: 44, whiteSpace: "nowrap" };
const thPack = { ...thItem, width: 1, padding: "6px 8px" };
const qtyNumCell = {
  fontSize: 13,
  color: "#1A2E2B",
  padding: "6px 8px",
  textAlign: "right",
  whiteSpace: "nowrap",
  fontVariantNumeric: "tabular-nums",
};
const packCell = { padding: "6px 0 6px 10px", whiteSpace: "nowrap" };
// The pack-unit chip (matches the mockup John approved): amber pill, teal ink.
const unitChip = {
  display: "inline-block",
  fontSize: 12,
  fontWeight: 600,
  color: "#00695C",
  background: "#FFF4D6",
  border: "1px solid #EAC44E",
  borderRadius: 5,
  padding: "1px 7px",
  whiteSpace: "nowrap",
};

// A picker's order number is appended to the SO line comment on completion as
// "… — Order No. - X"; strip that tail so the breakdown display stays clean.
const ORDER_NO_TAIL = /\s*—\s*Order No\. -.*$/;

// Parse the SO line comment ("Black Biros: 1; Wooden Rulers: 2") into item/qty rows.
// A single label with no "item: qty" (a pack year, or the reimbursement text) yields
// one row with a blank qty.
function parseBreakdown(comment) {
  const base = String(comment || "")
    .replace(ORDER_NO_TAIL, "")
    .trim();
  if (!base) return [];
  return base
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((part) => {
      const i = part.lastIndexOf(": ");
      return i === -1
        ? { item: part, qty: "" }
        : { item: part.slice(0, i).trim(), qty: part.slice(i + 2).trim() };
    });
}

function ROField({ label, value }) {
  return (
    <div>
      <div style={fieldLabel}>{label}</div>
      <div style={roValue}>{value || "—"}</div>
    </div>
  );
}

export function TopUpPickerView({ req, user, onDone, scopeSoNumber, assignedToOther }) {
  const [so, setSo] = React.useState(null);
  const [loaded, setLoaded] = React.useState(false);
  const [note, setNote] = React.useState("");
  const [push, setPush] = React.useState(null);
  const [rules, setRules] = React.useState([]);
  const [lines, setLines] = React.useState([]);
  const [amount, setAmount] = React.useState("");
  const [orderNumber, setOrderNumber] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState("");
  const [msgErr, setMsgErr] = React.useState(false);

  React.useEffect(
    function () {
      let alive = true;
      if (!scopeSoNumber) {
        setLoaded(true);
        return;
      }
      Promise.all([
        getSalesOrderByNumber(scopeSoNumber),
        getStockTopUpHeader(req.id),
        getPushByOrderNumber(scopeSoNumber),
        listTopUpSoRules(),
        listStockTopUpLines(req.id),
      ]).then(function ([s, hdr, p, r, ls]) {
        if (!alive) return;
        setSo(s);
        setNote(hdr?.note || "");
        setPush(p);
        setRules(r || []);
        setLines(ls || []);
        const soLine = (s?.lines || []).find(function (l) {
          return l.product_code && l.product_code !== "DISCOUNT";
        });
        const up = Number(soLine?.unit_price);
        // Pre-fill the amount only when the SO line already carries one (the $150
        // reimbursement); the summary/pack lines start at 0 for the picker to fill.
        setAmount(up > 0 ? String(up) : "");
        setLoaded(true);
      });
      return function () {
        alive = false;
      };
    },
    [scopeSoNumber, req.id]
  );

  // The SO's one orderable line (non-DISCOUNT) — carries the product code.
  const line = React.useMemo(
    function () {
      return (
        (so?.lines || []).find(function (l) {
          return l.product_code && l.product_code !== "DISCOUNT";
        }) || null
      );
    },
    [so]
  );
  // The SO's rule (matched by its base suffix) tells us the section + how to display it.
  const rule = React.useMemo(
    function () {
      return (rules || []).find((r) => r.suffix === push?.suffix) || null;
    },
    [rules, push]
  );
  const isBulk = rule?.line_mode === "summary";
  // The itemized breakdown: for a bulk (summary) SO, from the section's APPROVED lines
  // (the SO line comment is kept empty now); for a pack / reimbursement, the single
  // item from the SO line comment.
  const breakdown = React.useMemo(
    function () {
      if (isBulk && rule) {
        return (lines || [])
          .filter(
            (l) => l.section === rule.section && l.amount == null && Number(l.approved_qty ?? 0) > 0
          )
          .map((l) => ({
            item: lineLabel(l),
            qty: String(l.approved_qty ?? ""),
            section: l.section,
          }));
      }
      return parseBreakdown(line?.line_comments);
    },
    [isBulk, rule, lines, line]
  );
  const breakdownCols = React.useMemo(
    function () {
      if (breakdown.length <= 1) return [{ key: "colA", rows: breakdown }];
      const mid = Math.ceil(breakdown.length / 2);
      return [
        { key: "colA", rows: breakdown.slice(0, mid) },
        { key: "colB", rows: breakdown.slice(mid) },
      ];
    },
    [breakdown]
  );
  const productCode = line?.product_code || "";
  const soTitle = so?.header?.customer_reference || "Stock Top-up";
  // A single-item SO (a Stationery Pack, or the $150 reimbursement) uses the fit-out-
  // style card; a bulk (summary) SO shows the itemized breakdown table.
  const singleItem = rule ? !isBulk : breakdown.length <= 1;

  const allReady = Number(amount) > 0 && String(orderNumber).trim() !== "";

  async function onCompleted() {
    setSaving(true);
    setMsg("");
    setMsgErr(false);
    // Re-check at the click that this SO's task is still assigned to this account
    // (same guard as PickerRequestView). Checked BEFORE any save or release.
    const held = await findPickerLockTask(req.id, scopeSoNumber);
    if (!held || !user?.id || held.assigned_to_id !== user.id) {
      setSaving(false);
      setMsgErr(true);
      setMsg(NOT_YOUR_TASK_MSG);
      return;
    }
    // Finalise the SO line BEFORE releasing the held push, so the payload carries the
    // picker's ACTUAL price + order number. Both best-effort (never throw). One line.
    await updateSoLinePrices(scopeSoNumber, [amount]);
    await appendOrderNumbersToSoLines(scopeSoNumber, [orderNumber]);
    const released = await releaseHeldPushByOrderNumber(scopeSoNumber);
    if (released.error) {
      setSaving(false);
      setMsg(`Edits saved, but release failed: ${released.error.message}`);
      return;
    }
    // Flip THIS SO's picker task to Actioned (assignee-scoped so the db-60 guard passes).
    // Safety net: 0 rows updated = task NOT closed — warn, and skip the audit entry.
    let taskClosed = false;
    if (user?.id) {
      const now = new Date().toISOString();
      const done = await completePickerTaskBySoNumber(req.id, scopeSoNumber, user.id, now);
      taskClosed = !done.error && (done.data?.length ?? 0) > 0;
      if (taskClosed) {
        logAudit(
          "picker_task_actioned",
          { request_id: req.id, ref: req.refNo, so_number: scopeSoNumber, by: user.shelterName },
          req.id
        );
      }
    }
    // Order Confirmation comment/email to the service (once per day per type). Best-effort.
    await postOrderConfirmationOnce(req, user?.shelterName, "Stock Top-up");
    setSaving(false);
    if (!taskClosed) {
      setMsgErr(true);
      setMsg(TASK_NOT_CLOSED_MSG);
      return;
    }
    setMsg("Completed — the sales order has been released ✓");
    if (onDone) setTimeout(onDone, 900);
  }

  if (!loaded) {
    return <div style={{ padding: 24, fontSize: 13, color: "#8AA5A1" }}>Loading…</div>;
  }
  if (!scopeSoNumber || !so) {
    return (
      <div style={{ padding: 20, color: "#607D79", fontSize: 14 }}>
        Open a top-up order from the Pickers List to process it.
      </div>
    );
  }

  return (
    <div>
      {assignedToOther && (
        <div
          style={{
            background: "#FFF8E1",
            border: "1px solid #FFB300",
            borderRadius: 8,
            padding: "8px 14px",
            marginBottom: 14,
            fontSize: 13,
            fontWeight: 600,
            color: "#795548",
          }}
        >
          Assigned to {assignedToOther} — view only. Release it from the Pickers List to take it on.
        </div>
      )}

      {/* General Information */}
      <div
        style={{
          border: `1px solid ${BORDER}`,
          borderRadius: 10,
          overflow: "hidden",
          marginBottom: 14,
        }}
      >
        <div
          style={{
            background: BLUE,
            color: "white",
            padding: "0 18px",
            height: 48,
            display: "flex",
            alignItems: "center",
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          General Information
        </div>
        <div style={{ padding: "14px 16px" }}>
          {[
            ["DFV Service Ref", req.shelterRef],
            ["DFV Service", req.shelterName],
            ["Contact Name", req.contactName],
            [
              "Mobile / Phone",
              [req.mobile ? formatPhone(req.mobile) : "", req.phone ? formatPhone(req.phone) : ""]
                .filter(Boolean)
                .join("  ·  "),
            ],
          ].map(function (pair) {
            return pair[1] ? (
              <div
                key={pair[0]}
                style={{
                  display: "flex",
                  marginBottom: 6,
                  paddingBottom: 6,
                  borderBottom: `1px solid ${BORDER}`,
                }}
              >
                <div style={{ ...fieldLabel, width: 120, flexShrink: 0, marginBottom: 0 }}>
                  {pair[0]}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1A2E2B" }}>{pair[1]}</div>
              </div>
            ) : null;
          })}
          <div style={{ marginTop: 4 }}>
            <div style={fieldLabel}>Delivery</div>
            <div style={{ fontSize: 13, color: "#1A2E2B", lineHeight: 1.5 }}>
              {req.deliveryAddress || "—"}
            </div>
          </div>
          {/* The delivery address's instruction, as copied onto this SO at Confirm & Send. */}
          <DeliveryInstruction text={so?.header?.delivery_instruction} layout="stacked" />
        </div>
      </div>

      {note && (
        <div
          style={{
            background: "#fff",
            border: `1px solid ${BORDER}`,
            borderRadius: 10,
            padding: "12px 16px",
            marginBottom: 14,
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
            Notes from the service
          </div>
          <div style={{ fontSize: 13, color: "#1A2E2B", whiteSpace: "pre-wrap" }}>{note}</div>
        </div>
      )}

      {/* The ONE order card */}
      <div style={{ border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden" }}>
        <div
          style={{
            background: "#E8F4EA",
            padding: "10px 16px",
            borderBottom: `1px solid ${BORDER}`,
            fontSize: 14,
            color: "#1A2E2B",
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <strong>{soTitle}</strong>
          <span style={{ color: "#607D79", fontWeight: 600 }}>{scopeSoNumber}</span>
        </div>
        <div style={{ padding: 16 }}>
          {singleItem ? (
            /* Single-item SO (a Stationery Pack, or the $150 reimbursement): the SAME
               field layout as the fit-out picker card — Item / Cost / SO No. / Code / Order. */
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1.3fr 1fr 1fr 1fr", gap: 12 }}>
              <ROField label="Item" value={breakdown[0]?.item || soTitle} />
              <div>
                <div style={fieldLabel}>Actual Cost - GST Excluded Price</div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  placeholder="0.00"
                  onChange={function (e) {
                    setAmount(e.target.value);
                  }}
                  style={editInput}
                />
              </div>
              <ROField label="SO No." value={scopeSoNumber} />
              <ROField label="Product Code" value={productCode} />
              <div>
                <div style={fieldLabel}>Order No.</div>
                <input
                  type="text"
                  value={orderNumber}
                  placeholder="Enter once ordered"
                  onChange={function (e) {
                    setOrderNumber(e.target.value);
                  }}
                  style={editInput}
                />
              </div>
            </div>
          ) : (
            <>
              {/* Bulk section: the itemized breakdown, in request order, split into TWO
                  columns (like the capture form) so a big bulk order is easy to read. */}
              <div style={fieldLabel}>Items to order</div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: breakdownCols.length === 2 ? "1fr 1fr" : "1fr",
                  gap: 24,
                  marginBottom: 14,
                }}
              >
                {breakdownCols.map(function (col) {
                  return (
                    <table key={col.key} style={{ borderCollapse: "collapse", width: "100%" }}>
                      <thead>
                        <tr>
                          <th style={thItem}>Item</th>
                          <th style={thQty}>Qty</th>
                          <th style={thPack} />
                        </tr>
                      </thead>
                      <tbody>
                        {col.rows.map(function (b) {
                          const unit = sectionUnitLabel(b.section);
                          return (
                            <tr key={b.item} style={{ borderBottom: "1px solid #EEF3F2" }}>
                              <td style={{ fontSize: 13, color: "#1A2E2B", padding: "6px 8px" }}>
                                {b.item}
                              </td>
                              <td style={qtyNumCell}>{b.qty || "—"}</td>
                              <td style={packCell}>
                                {unit ? <span style={unitChip}>{unit}</span> : null}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  );
                })}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <ROField label="Product Code" value={productCode} />
                <div>
                  <div style={fieldLabel}>Actual Cost (summary)</div>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    placeholder="0.00"
                    onChange={function (e) {
                      setAmount(e.target.value);
                    }}
                    style={editInput}
                  />
                </div>
                <div>
                  <div style={fieldLabel}>Order No.</div>
                  <input
                    type="text"
                    value={orderNumber}
                    placeholder="Enter once ordered"
                    onChange={function (e) {
                      setOrderNumber(e.target.value);
                    }}
                    style={editInput}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 14, alignItems: "center", marginTop: 12 }}>
        {!allReady && (
          <span style={{ fontSize: 12, color: "#607D79" }}>
            Enter the actual cost and the order number to enable Completed.
          </span>
        )}
        {msg && (
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: msgErr || msg.toLowerCase().includes("fail") ? "#C62828" : "#2E7D32",
            }}
          >
            {msg}
          </span>
        )}
        <button
          type="button"
          disabled={!allReady || saving || !!assignedToOther}
          onClick={onCompleted}
          style={{
            marginLeft: "auto",
            background: "#2E7D32",
            color: "white",
            border: "none",
            borderRadius: 8,
            padding: "10px 24px",
            fontSize: 15,
            fontWeight: 700,
            fontFamily: "inherit",
            cursor: allReady && !saving && !assignedToOther ? "pointer" : "not-allowed",
            opacity: allReady && !saving && !assignedToOther ? 1 : 0.5,
          }}
        >
          {saving ? "Working..." : "Completed"}
        </button>
      </div>
    </div>
  );
}
